/**
 * deposito-base.ts
 * The Depósito screens are mounted under BOTH modules — /compras/deposito and
 * /servicios/deposito render the same components (odd/tasks/compras-deposito.md
 * S1, L5). The sidebar scopes items by the active module's path prefix, so each
 * mount keeps its own module context and every internal link must stay inside
 * the base it was opened from. This is the one place that turns a base + page
 * into a route name; routes, tabs, tiles and rows all go through it.
 */
import { computed, type ComputedRef } from 'vue';
import { useRoute } from 'vue-router';

export const DEPOSITO_BASES = ['compras', 'servicios'] as const;
export type DepositoBase = (typeof DEPOSITO_BASES)[number];
export type DepositoPage = 'inicio' | 'recibir' | 'entregar' | 'contar' | 'mover' | 'movimientos' | 'item';

/** URL path of a base's Depósito home, e.g. `/servicios/deposito`. */
export function depositoBasePath(base: DepositoBase): string {
  return `/${base}/deposito`;
}

/** Unique route name of a Depósito page under a base, e.g. `servicios-deposito-recibir`. */
export function depositoRouteName(base: DepositoBase, page: DepositoPage): string {
  if (page === 'item') return `${base}-stock-item`;
  if (page === 'inicio') return `${base}-deposito`;
  return `${base}-deposito-${page}`;
}

/** The base a route was mounted under (route meta `depositoBase`); Compras when absent. */
export function depositoBaseOf(route: { meta?: Record<string, unknown> } | undefined): DepositoBase {
  const base = route?.meta?.['depositoBase'];
  return base === 'servicios' ? 'servicios' : 'compras';
}

/** Current base + a route-name builder bound to it, for components rendered inside either mount. */
export function useDepositoBase(): { base: ComputedRef<DepositoBase>; routeName: (page: DepositoPage) => string } {
  const route = useRoute();
  const base = computed(() => depositoBaseOf(route));
  return { base, routeName: (page) => depositoRouteName(base.value, page) };
}
